/**
 * useReadingStats — the reader's own streak and totals.
 *
 * Kept in users/{uid}/private/stats, which only its owner can read or write
 * (the same rule as every private document). Nothing here is shown to anyone
 * else, and nothing here is used to rank or judge the reader.
 *
 * THE STREAK IS KIND ON PURPOSE
 *
 * A day counts when the day's briefing is finished. One rest day is allowed
 * between finished days, so a night shift, a day off or a briefing finished
 * just after midnight does not wipe out a run. Missing more simply starts a
 * new streak — the best run is kept and shown, and nothing ever scolds anyone
 * for having a life. Habit, not guilt.
 */

import { useEffect, useState } from 'react';
import { doc, onSnapshot, setDoc, increment, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

/** Papers-read totals worth a small celebration. */
export const MILESTONES = [10, 25, 50, 100, 250, 500, 1000];

/** Today's date in the UK, the briefing's own day. */
export function londonDay(d = new Date()) {
  return d.toLocaleDateString('en-CA', { timeZone: 'Europe/London' });
}

function dayDiff(from, to) {
  return Math.round((Date.parse(`${to}T12:00:00Z`) - Date.parse(`${from}T12:00:00Z`)) / 86400000);
}

/** The streak once today is finished, allowing one rest day between days. */
export function nextStreak(stats, today) {
  const last = stats?.lastDay;
  if (last === today) return { streak: stats?.streak ?? 1, changed: false };
  const gap = last ? dayDiff(last, today) : Infinity;
  const streak = gap >= 1 && gap <= 2 ? (Number(stats?.streak) || 0) + 1 : 1;
  return { streak, changed: true };
}

/** The streak as it stands right now: still alive while within the rest day. */
export function currentStreak(stats, today = londonDay()) {
  if (!stats?.lastDay) return 0;
  const gap = dayDiff(stats.lastDay, today);
  return gap <= 2 ? Number(stats.streak) || 0 : 0;
}

/** The last seven days, oldest first, each marked done or not. */
export function lastSevenDays(stats, today = londonDay()) {
  const done = new Set(stats?.days ?? []);
  const base = Date.parse(`${today}T12:00:00Z`);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(base - (6 - i) * 86400000);
    const key = d.toISOString().slice(0, 10);
    return { key, done: done.has(key), label: d.toLocaleDateString('en-GB', { weekday: 'narrow', timeZone: 'UTC' }) };
  });
}

const statsRef = (uid) => doc(db, 'users', uid, 'private', 'stats');

/** Live stats for the signed-in reader. undefined while loading, null when none. */
export function useReadingStats(uid) {
  const [stats, setStats] = useState(undefined);
  useEffect(() => {
    if (!uid) { setStats(null); return undefined; }
    return onSnapshot(
      statsRef(uid),
      (snap) => setStats(snap.exists() ? snap.data() : null),
      () => setStats(null)
    );
  }, [uid]);
  return stats;
}

/** One more paper read. */
export function recordPaperRead(uid) {
  return setDoc(statsRef(uid), { papersRead: increment(1), updatedAt: serverTimestamp() }, { merge: true });
}

/** Today's briefing finished. Returns the new streak, and whether it moved. */
export async function recordDayComplete(uid, stats) {
  const today = londonDay();
  const { streak, changed } = nextStreak(stats, today);
  if (!changed) return { streak, changed };
  const days = [...(stats?.days ?? []).filter((d) => d !== today), today].slice(-35);
  await setDoc(statsRef(uid), {
    streak,
    best: Math.max(Number(stats?.best) || 0, streak),
    lastDay: today,
    days,
    updatedAt: serverTimestamp(),
  }, { merge: true });
  return { streak, changed };
}
